import { requestJson, type ErrorFactory } from './httpClient';
import { workspaceStore } from '~/config/workspaceStore';
import type {
  CreateWorkspaceDTO,
  UpdateWorkspaceDTO,
  Workspace,
  WorkspaceStatsDTO,
} from '~/types/workspace';

export class WorkspaceApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public bodyMessage?: string,
    /** Kod błędu z `ErrorResponseDTO.code` (np. `SUBSCRIPTION_LIMIT_WORKSPACES`). */
    public code?: string
  ) {
    super(message);
    this.name = 'WorkspaceApiError';
  }
}

const workspaceError =
  (fallbackPrefix: string): ErrorFactory =>
  (status, body) =>
    new WorkspaceApiError(
      body.rawMessage || `${fallbackPrefix}: ${status}`,
      status,
      body.rawMessage,
      body.ruleCode
    );

/** GET /api/workspaces — no workspaceId query. */
export async function fetchWorkspaces(): Promise<Workspace[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    return await requestJson<Workspace[]>('/api/workspaces', {
      signal: controller.signal,
      createError: workspaceError('Failed to fetch workspaces'),
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new WorkspaceApiError('Workspace request timed out', 408);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function fetchWorkspaceById(workspaceId: number): Promise<Workspace> {
  return requestJson<Workspace>(`/api/workspaces/${workspaceId}`, {
    createError: workspaceError('Failed to load workspace'),
  });
}

/** GET /api/workspaces/{workspaceId}/stats — ACTIVE workspace owned by user only (404 otherwise). */
export async function fetchWorkspaceStats(workspaceId: number): Promise<WorkspaceStatsDTO> {
  const data = await requestJson<unknown>(`/api/workspaces/${workspaceId}/stats`, {
    createError: workspaceError('Failed to load workspace stats'),
  });
  const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

  return {
    assetsCount: Number(obj.assetsCount ?? 0),
    deadlinesCount: Number(obj.deadlinesCount ?? 0),
    documentsCount: Number(obj.documentsCount ?? 0),
  };
}

export function createWorkspace(body: CreateWorkspaceDTO): Promise<Workspace> {
  return requestJson<Workspace>('/api/workspaces', {
    method: 'POST',
    body,
    createError: workspaceError('Failed to create workspace'),
  });
}

export function updateWorkspace(workspaceId: number, body: UpdateWorkspaceDTO): Promise<Workspace> {
  return requestJson<Workspace>(`/api/workspaces/${workspaceId}`, {
    method: 'PUT',
    body,
    createError: workspaceError('Failed to update workspace'),
  });
}

export async function deleteWorkspace(workspaceId: number): Promise<void> {
  await requestJson<void>(`/api/workspaces/${workspaceId}`, {
    method: 'DELETE',
    createError: workspaceError('Failed to delete workspace'),
  });
}

/** Loads workspaces into store and marks bootstrap ready. Call after auth sync. */
export async function bootstrapWorkspaces(): Promise<void> {
  try {
    const list = await fetchWorkspaces();
    workspaceStore.loadWorkspaces(list);
    workspaceStore.setBootstrapError(null);
  } catch (error) {
    workspaceStore.loadWorkspaces([]);
    const message = error instanceof Error ? error.message : 'Failed to load workspaces';
    workspaceStore.setBootstrapError(message);
  } finally {
    workspaceStore.setBootstrapReady(true);
  }
}

/** Refetch list and merge into store (keeps selection when possible). */
export async function refreshWorkspaces(): Promise<void> {
  const list = await fetchWorkspaces();
  workspaceStore.loadWorkspaces(list);
}
